import {Inject, inject, Injectable} from '@angular/core';
import {EventTypes, OidcSecurityService, PublicEventsService} from 'angular-auth-oidc-client';
import {HttpClient} from '@angular/common/http';
import {filter, firstValueFrom, Observable, of, switchMap} from "rxjs";

@Injectable({
    providedIn: 'root'
})
export class AuthService {

    private oidcSecurityService = inject(OidcSecurityService);
    private readonly eventService = inject(PublicEventsService);

    private events$ = this.eventService.registerForEvents();

    constructor(@Inject(HttpClient) private readonly http: HttpClient) {
      this.events$
        .pipe(filter(
          (notification) =>
            notification.type === EventTypes.TokenExpired
        ))
        .subscribe(()=>{
          let url = `/upload/file/logout`;
          firstValueFrom(this.http.get(url, {observe: 'response', responseType: 'text'}))
            .catch(this.handleError);
          sessionStorage.removeItem('session');
          window.location.replace('/logout.html');
        });
    }

    isAuthenticated() {
        return this.oidcSecurityService.isAuthenticated$;
    }

    getUser() {
        return this.oidcSecurityService.userData$;
    }

    login() {
        return this.oidcSecurityService.authorize();
    }

    initializeUploaderSession() {
      let url = `/upload/file/initSession`;
      return firstValueFrom(this.http.post(url, {observe: 'response', responseType: 'text'}))
        .then(response => sessionStorage.setItem('session', 'true'))
        .catch(this.handleError);
    }

    logout() {
        let url = `/upload/file/logout`;
        firstValueFrom(this.http.get(url, {observe: 'response', responseType: 'text'}))
          .catch(this.handleError);
        sessionStorage.removeItem('session')
        return this.oidcSecurityService.logoff();
    }

    getAccessToken(): Observable<string> {
        return this.oidcSecurityService.getAccessToken();
    }

    checkAuth() {
        return this.oidcSecurityService.checkAuth()
            .pipe(switchMap(response =>
                of({response, callbackError: checkAuthCallbackError()} as CheckAuthResult )));
    }

  private handleError(error: any): Promise<any> {
    console.error('An error occurred', error);
    return Promise.reject(error.message || error);
  }
}

export function checkAuthCallbackError(){
  const params = new URLSearchParams(window.location.search);

  const error = params.get('error');
  const errorDescription = params.get('error_description');
  return {error, errorDescription};
}

export interface CheckAuthResult {
    response: {
        isAuthenticated: boolean;
        userData: any;
        accessToken: string;
        idToken: string;
        configId?: string;
        errorMessage?: string;
    },
    callbackError: { error: string; errorDescription: string; }
}
